<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import interviewService from "@/modules/worker/services/interviewService";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import TextEditorModal from "@/dsComponents/modals/TextEditorModal.vue";

interface InterviewEditModalProps {
    open?: boolean;
    workerId: string;
    interviewId: string;
    initialSummary: string;
    onOptimistic: {
        update?: (summary: string) => void;
        rollback?: () => void;
    };
}

const props = withDefaults(defineProps<InterviewEditModalProps>(), {
    open: true,
});

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const { toastSuccess, toastError } = useDefaultToast();

const summaryText = ref(props.initialSummary);
const isSaving = ref(false);

const trimmedSummary = computed(() => summaryText.value.trim());
const isSaveDisabled = computed(
    () => trimmedSummary.value.length === 0 || trimmedSummary.value === props.initialSummary.trim()
);

watch(
    () => [props.open, props.initialSummary] as const,
    ([isOpen]) => {
        if (isOpen) {
            summaryText.value = props.initialSummary;
        }
    }
);

const handleSubmit = async () => {
    if (isSaving.value || isSaveDisabled.value) return;
    const summary = trimmedSummary.value;
    isSaving.value = true;
    props.onOptimistic.update?.(summary);
    try {
        await interviewService.updateInterview(props.workerId, props.interviewId, { summary });
        toastSuccess(t("page.interim.profile.interview.toast.updateSuccess"));
        emit("close");
    } catch {
        props.onOptimistic.rollback?.();
        toastError();
    } finally {
        isSaving.value = false;
    }
};
</script>

<template>
    <TextEditorModal
        v-model="summaryText"
        :placeholder="t('page.interim.profile.interview.placeholder')"
        :open="open"
        :title="t('page.interim.profile.interview.editTitle')"
        :disabled="isSaving"
        disable-overlay-close
        height="min(760px, 90vh)"
        @close="emit('close')"
    >
        <template #footer-end>
            <ButtonAlt
                size="sm"
                type="neutral"
                variant="stroke"
                :label="t('global.actions.cancel')"
                :disabled="isSaving"
                @click="emit('close')"
            />
            <ButtonAlt
                size="sm"
                variant="filled"
                type="primary"
                :label="t('global.actions.save')"
                :loading="isSaving"
                :disabled="isSaveDisabled"
                @click="handleSubmit"
            />
        </template>
    </TextEditorModal>
</template>
